import { ApolloError, ApolloQueryResult, useMutation, useQuery } from '@apollo/client';
import { Context } from '@blackbird/ui-base';
import Link from '@blackbird/ui-base/link';
import {
  IntegratedFiltering,
  IntegratedPaging,
  IntegratedSorting,
  PagingState,
  SearchState,
  SortingDirection,
  SortingState,
} from '@devexpress/dx-react-grid';
import { Table as TableBase } from '@devexpress/dx-react-grid';
import {
  Grid,
  PagingPanel,
  SearchPanel,
  Table,
  TableHeaderRow,
  TableProps,
  Toolbar,
} from '@devexpress/dx-react-grid-material-ui';
import {
  AddCircle as AddCircleIcon,
  Delete,
  OpenInBrowser as OpenInBrowserIcon,
  SettingsApplications as SettingsApplicationsIcon,
} from '@mui/icons-material';
import { Button, IconButton, Paper, TableContainer, Tooltip, Typography } from '@mui/material';
import * as Schema from 'generated/graphql/schema';
import { FunctionComponent, useCallback, useContext, useMemo, useState } from 'react';
import * as WebTabs from 'react-web-tabs';
import { makeStyles } from 'tss-react/mui';

import ConfirmDialog from '@/components/dialogs/confirm-dialog';
import { RequiredDeviceFields } from '@/components/dialogs/create-peripheral/create-peripheral-content';
import CreatePeripheralDialog from '@/components/dialogs/create-peripheral/create-peripheral-dialog';
import { deletePeripheral } from '@/graphql/mutations';
import { administrationDetailedGroups, administrationDevicePeripherals } from '@/graphql/queries';
import { useTranslation, useURLQuery } from '@/hooks';
import { isTruthy } from '@/types';
import SensorSettingsDialog from '@/views/dialogs/sensor-settings/sensor-settings-dialog';

const useStyles = makeStyles()((theme) => ({
  tab: {
    background: 'transparent',
    border: 0,
    fontFamily: theme.typography.fontFamily,
    display: 'inline-block',
    textAlign: 'left',
    fontSize: 'inherit',
    padding: '0.8rem 2rem',
    margin: '0.2rem 0',
    transition: 'background 0.3s cubic-bezier(0.22, 0.61, 0.36, 1)',
    [theme.breakpoints.down('sm')]: {
      padding: '0.8rem',
    },
    '&:focus': {
      outline: 0,
      backgroundColor: '#f4f4f4',
    },
    '&[aria-selected="false"]:hover': {
      outline: 0,
      backgroundColor: '#f4f4f4',
    },
    '&[aria-selected="true"]': {
      position: 'relative',
      '&:after': {
        content: '""',
        position: 'absolute',
        right: '-1px',
        top: 0,
        height: '100%',
        borderRight: `3px solid ${theme.palette.primary.main}`,
      },
    },
  },
  invalidTab: {
    borderLeft: '2px',
    borderColor: 'red',
    borderStyle: 'solid',
  },
  tabPanel: {
    padding: '0px 20px',
    height: '100%',
    width: '100%',
  },
  denseTable: {
    '& th': {
      padding: '6px 24px 6px 16px !important',
    },
    '& td': {
      padding: '6px 24px 6px 16px !important',
    },
  },
  densePager: {
    padding: '0px',
  },
  denseToolbar: {
    '& > div': {
      marginLeft: '0px !important',
    },
    display: 'flex',
    justifyContent: 'flex-end',
    paddingRight: theme.spacing(),
  },
  iconMargin: {
    marginRight: theme.spacing(),
  },
}));

const DenseTableComponent: FunctionComponent<TableProps> = (props) => {
  const { classes } = useStyles();

  return <Table.Table {...props} className={classes.denseTable} />;
};

const DenseToolbarComponent: FunctionComponent<Toolbar.RootProps & { onClickAdd: () => void }> = (props) => {
  const { classes } = useStyles();
  const [t] = useTranslation(['device']);

  const { children, onClickAdd, ...restProps } = props;

  return (
    <Toolbar.Root {...restProps} className={classes.denseToolbar}>
      {children}
      <div style={{ marginRight: 'auto' }} />
      <Tooltip title={t(['device:addSensorTooltip'], { defaultValue: 'Add a new sensor for this device' })}>
        <Button color="primary" aria-label="add" onClick={onClickAdd}>
          <AddCircleIcon className={classes.iconMargin} />
          {t(['device:addSensor'], { defaultValue: 'Add Sensor' })}
        </Button>
      </Tooltip>
    </Toolbar.Root>
  );
};

const DensePagingPanel: FunctionComponent<PagingPanel.ContainerProps> = (props) => {
  const { classes } = useStyles();

  return <PagingPanel.Container {...props} className={classes.densePager} />;
};

export const Tab: FunctionComponent = () => {
  const [t] = useTranslation(['shared']);
  const { classes } = useStyles();

  // TODO: This is left here to make validation easy in the next PR, when there
  // will be fields that can actually be invalid (IP address setup)
  const isValid = true;

  return (
    <WebTabs.Tab className={isValid ? classes.tab : `${classes.tab} ${classes.invalidTab}`} tabFor="sensors">
      <Typography variant="button">{t(['device:sensors'], { defaultValue: 'Sensors' })}</Typography>
    </WebTabs.Tab>
  );
};

interface Properties {
  peripherals: NonNullable<Schema.AdministrationDevicePeripheralsQuery['device']>['peripherals'];
  device?: RequiredDeviceFields;
  loading: boolean;
  refetchPeripherals: () => Promise<ApolloQueryResult<Schema.AdministrationDevicePeripheralsQuery>>;
}

export const TabPanel: React.FunctionComponent<Properties> = (props) => {
  const { classes } = useStyles();
  const [t] = useTranslation();
  const { softwareId = '' } = useURLQuery();
  const snackbar = useContext(Context.Snackbar);

  const columns = useMemo(
    () => [
      { name: 'index', title: t(['shared:tagName'], { defaultValue: 'Tag name' }) },
      { name: 'name', title: t(['shared:name'], { defaultValue: 'Name' }) },
      { name: 'description', title: t(['shared:description'], { defaultValue: 'Description' }) },
      { name: 'actionButtons', title: t(['shared:actions'], { defaultValue: 'Actions' }) },
    ],
    [],
  );

  const [tableColumnExtensions] = useState<TableBase.ColumnExtension[]>([
    { columnName: 'actionButtons', align: 'right' },
  ]);

  const [sorting, setSorting] = useState([{ columnName: 'softwareId', direction: 'asc' as SortingDirection }]);
  const [pageSizes] = useState([5, 10, 25, 50]);
  const [searchValue, setSearchState] = useState('');
  const [createPeripheralsDialogOpen, setCreatePeripheralsDialogOpen] = useState(false);
  const [showConfirmDeleteDialog, setShowConfirmDeleteDialog] = useState(false);
  const [peripheralIdToDelete, setPeripheralIdToDelete] = useState<string | undefined>(undefined);
  const [sensorSettingsState, setSensorSettingsState] = useState<{ open: boolean; peripheralId: string | null }>({
    open: false,
    peripheralId: null,
  });

  const { data: groupsData } = useQuery<Schema.AdministrationDetailedGroupsQuery>(administrationDetailedGroups, {
    ssr: false,
  });

  const [deletePeripheralMutation] = useMutation<Schema.DeletePeripheralMutation>(deletePeripheral, {
    onError: (error: ApolloError) => snackbar.spawn(error.message, 'error'),
    onCompleted: () => {
      props.refetchPeripherals();
    },
  });

  const openDialog = useCallback(() => {
    setCreatePeripheralsDialogOpen(true);
  }, [setCreatePeripheralsDialogOpen]);

  const closeDialog = useCallback(() => {
    props.refetchPeripherals();
    setCreatePeripheralsDialogOpen(false);
  }, [setCreatePeripheralsDialogOpen]);

  const handleSensorSettingsChange = (open: boolean) => {
    setSensorSettingsState({ ...sensorSettingsState, open });
  };

  const handleSensorSettingsOpen = (peripheralId: string | undefined) => {
    if (typeof peripheralId === 'undefined') {
      return;
    }
    setSensorSettingsState({ open: true, peripheralId });
  };

  const onClickDeletePeripheral = (peripheralId: string) => {
    setShowConfirmDeleteDialog(true);
    setPeripheralIdToDelete(peripheralId);
  };

  const onConfirmDeletePeripheral = async () => {
    if (!peripheralIdToDelete) {
      console.error('Missing peripheral ID!');
      return;
    }
    await deletePeripheralMutation({ variables: { peripheralId: peripheralIdToDelete } });
    setShowConfirmDeleteDialog(false);
    setPeripheralIdToDelete(undefined);
  };

  const rows = (props.peripherals || []).filter(isTruthy).map((peripheral) => {
    const actionButtons = (
      <>
        <Link
          href={{
            pathname: '/sensor',
            query: {
              peripheralId: `${peripheral.peripheralId}`,
              tab: 'live',
            },
          }}
        >
          <Tooltip title={t(['line:openSensor'], { defaultValue: 'Open Sensor' }) as unknown as string}>
            <IconButton size="small">
              <OpenInBrowserIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Link>
        <Tooltip
          title={
            t(['iam:configureThisSensor'], {
              defaultValue: 'Configure this sensor',
            }) as unknown as string
          }
        >
          <IconButton
            size="small"
            onClick={() => {
              handleSensorSettingsOpen(peripheral.peripheralId);
            }}
          >
            <SettingsApplicationsIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete peripheral">
          <IconButton size="small" onClick={() => onClickDeletePeripheral(peripheral.peripheralId)}>
            <Delete fontSize="inherit" />
          </IconButton>
        </Tooltip>
      </>
    );

    return {
      ...peripheral,
      actionButtons,
    };
  });

  const i18nMessages = {
    noData: props.loading
      ? t(['shared:loading'], { defaultValue: 'Loading...' })
      : t(['shared:noSensors'], { defaultValue: 'No sensors' }),
  };

  const toolbarRootComponent = useCallback(
    (props) => <DenseToolbarComponent {...props} onClickAdd={openDialog} />,
    [openDialog],
  );

  return (
    <WebTabs.TabPanel className={classes.tabPanel} tabId="sensors">
      <TableContainer component={Paper}>
        <Grid rows={rows} columns={columns}>
          <SearchState value={searchValue} onValueChange={setSearchState} />
          <IntegratedFiltering />
          <SortingState sorting={sorting} onSortingChange={setSorting} />
          <IntegratedSorting />
          <PagingState defaultCurrentPage={0} defaultPageSize={5} />
          <IntegratedPaging />
          <Table
            messages={i18nMessages}
            tableComponent={DenseTableComponent}
            columnExtensions={tableColumnExtensions}
          />
          <TableHeaderRow showSortingControls />
          <Toolbar rootComponent={toolbarRootComponent} />
          <SearchPanel />
          <PagingPanel pageSizes={pageSizes} containerComponent={DensePagingPanel} />
        </Grid>
      </TableContainer>
      {createPeripheralsDialogOpen ? (
        <CreatePeripheralDialog
          companyId={groupsData?.company.id}
          groups={groupsData?.company.groups ?? []}
          onDone={closeDialog}
          softwareId={softwareId}
          open={createPeripheralsDialogOpen}
          onClose={closeDialog}
          device={props.device}
        />
      ) : null}
      <SensorSettingsDialog
        dialogOpen={sensorSettingsState.open}
        peripheralId={sensorSettingsState.peripheralId}
        handleDialogChange={handleSensorSettingsChange}
        refetchQueries={[
          {
            query: administrationDevicePeripherals,
            variables: {
              uuid: softwareId,
            },
          },
        ]}
      />
      <ConfirmDialog
        open={showConfirmDeleteDialog}
        onClose={() => setShowConfirmDeleteDialog(false)}
        onConfirm={onConfirmDeletePeripheral}
        title={t(['device:deletePeripheral'], { defaultValue: 'Delete peripheral' })}
        content={t(['device:confirmPeripheralDelete'], {
          defaultValue: 'Are you sure you want to delete this peripheral?',
        })}
      />
    </WebTabs.TabPanel>
  );
};
